import { API_BASE_URL } from "../config";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Org = {
  id: number;
  name: string;
  short_name: string;
  domains: string[];
  allowed_sources: string[] | null;
  payg_credit_limit: number | null;
  source_limits: Record<string, Record<string, number>> | null;
  custom_credentials_secrets_manager_arn: string | null;
};

export type OrgListResponse = {
  orgs: Org[];
};

export type OrgDetailResponse = {
  org: Org;
};

export type AdminUser = {
  id: number;
  username: string;
  email: string;
  is_staff: boolean;
  is_active: boolean;
  organization_id: number | null;
  organization_short_name: string | null;
  allowed_sources: string[] | null;
  payg_credit_limit: number | null;
  source_limits: Record<string, Record<string, number>> | null;
};

export type OrgUsersResponse = {
  users: AdminUser[];
};

export type UserDetailResponse = {
  user: AdminUser;
};

// ---------------------------------------------------------------------------
// Orgs
// ---------------------------------------------------------------------------

export const fetchOrgs = async (
  accessToken: string,
  query?: string,
): Promise<OrgListResponse> => {
  const params = query ? `?q=${encodeURIComponent(query)}` : "";
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/orgs${params}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    throw new Error("Failed to load organizations.");
  }
  return res.json();
};

export const fetchOrg = async (
  accessToken: string,
  shortName: string,
): Promise<OrgDetailResponse> => {
  const res = await fetch(
    `${API_BASE_URL}/api/v1/admin/orgs/${shortName}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
  if (!res.ok) {
    throw new Error("Organization not found.");
  }
  return res.json();
};

export const createOrg = async (
  accessToken: string,
  data: { name: string; short_name: string; domains?: string[] },
): Promise<OrgDetailResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/orgs`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(
      body?.message
        ? (typeof body.message === "string" ? body.message : JSON.stringify(body.message))
        : "Failed to create organization.",
    );
  }
  return res.json();
};

export const updateOrg = async (
  accessToken: string,
  shortName: string,
  data: Partial<{ name: string; domains: string[] }>,
): Promise<OrgDetailResponse> => {
  const res = await fetch(
    `${API_BASE_URL}/api/v1/admin/orgs/${shortName}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(
      body?.message
        ? (typeof body.message === "string" ? body.message : JSON.stringify(body.message))
        : "Failed to update organization.",
    );
  }
  return res.json();
};

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export const fetchOrgUsers = async (
  accessToken: string,
  shortName: string,
  query?: string,
): Promise<OrgUsersResponse> => {
  const params = query ? `?q=${encodeURIComponent(query)}` : "";
  const res = await fetch(
    `${API_BASE_URL}/api/v1/admin/orgs/${shortName}/users${params}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
  if (!res.ok) {
    throw new Error("Failed to load users.");
  }
  return res.json();
};

export const createUser = async (
  accessToken: string,
  data: {
    username: string;
    email?: string;
    organization_short_name?: string;
    is_staff?: boolean;
    is_active?: boolean;
  },
): Promise<UserDetailResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/users`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(
      body?.message
        ? (typeof body.message === "string" ? body.message : JSON.stringify(body.message))
        : "Failed to create user.",
    );
  }
  return res.json();
};

export const fetchUser = async (
  accessToken: string,
  username: string,
): Promise<UserDetailResponse> => {
  const res = await fetch(
    `${API_BASE_URL}/api/v1/admin/users/${encodeURIComponent(username)}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
  if (!res.ok) {
    throw new Error("User not found.");
  }
  return res.json();
};

export const updateUser = async (
  accessToken: string,
  username: string,
  data: Partial<{
    email: string;
    organization_short_name: string | null;
    is_staff: boolean;
    is_active: boolean;
  }>,
): Promise<UserDetailResponse> => {
  const res = await fetch(
    `${API_BASE_URL}/api/v1/admin/users/${encodeURIComponent(username)}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(
      body?.message
        ? (typeof body.message === "string" ? body.message : JSON.stringify(body.message))
        : "Failed to update user.",
    );
  }
  return res.json();
};
